import {
  DEPOSIT_NETWORK_SHORT,
  formatDepositDate,
} from '../constants/depositData';
import { cardsApi } from '../api/services/cardsApi';
import {
  addDecimalStrings,
  decimalGte,
  formatDecimal,
  parseDecimalInput,
  scaledIntToNormal,
} from '../utils/decimalValues';
import { getCurrencyIconUrl } from '../utils/currencyIcon';

const DEPOSIT_SESSION_KEY = 'obank_deposit_session';
const POLL_INTERVAL_MS = 5000;
const USD_AMOUNT_DECIMALS = 2;

export const DEPOSIT_OUTCOMES = {
  SUCCESS: 'success',
  FAILED: 'failed',
};

const listeners = new Set();
let activeFlow = null;

function formatUsdAmount(value) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return '$0.00';
  }

  return formatDecimal(decimal, 2, {
    prefix: '$',
    minFractionDigits: 2,
  });
}

function formatNetworkName(network) {
  const normalized = String(network || '').trim().replace(/-/g, '').toUpperCase();

  return normalized.replace(/^([A-Z]+)(20)$/, '$1-$2');
}

function getCurrencyDecimals(currency) {
  const decimals = Number.parseInt(currency?.decimal_places, 10);

  return Number.isFinite(decimals) && decimals >= 0 ? decimals : 0;
}

function getDepositOperationKey(operation) {
  return String(
    operation?._id
    || operation?.id
    || operation?.tx_hash
    || [
      operation?.address,
      operation?.amount,
      operation?.amount_usd,
      operation?.currency?._id,
      operation?.network,
    ].filter(Boolean).join(':')
  );
}

function getOperationUsdAmount(operation) {
  return scaledIntToNormal(operation?.amount_usd, USD_AMOUNT_DECIMALS, {
    fixed: false,
    trimZeros: true,
  });
}

function getOperationAssetAmount(operation) {
  return scaledIntToNormal(operation?.amount, getCurrencyDecimals(operation?.currency), {
    fixed: false,
    trimZeros: true,
  });
}

function formatAssetAmount(value, asset, decimals = 6) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return `0 ${asset}`;
  }

  return `${formatDecimal(decimal, decimals)} ${asset}`;
}

function getExplorerUrl(operation) {
  const txHash = operation?.tx_hash;
  const network = operation?.network;
  const networks = operation?.currency?.networks?.total;
  const networkKey = String(network || '');
  const normalizedNetworkKey = networkKey.replace(/-/g, '');
  const explorerTemplate = networks?.[networkKey]?.explorer
    || networks?.[normalizedNetworkKey]?.explorer;

  if (!txHash || !explorerTemplate) {
    return '';
  }

  return String(explorerTemplate).replace('{tx_id}', txHash);
}

function getExplorerName(explorerUrl) {
  if (!explorerUrl) {
    return '';
  }

  try {
    const host = new URL(explorerUrl).hostname.replace(/^www\./, '');

    if (host.includes('etherscan')) return 'Etherscan';
    if (host.includes('tronscan')) return 'Tronscan';

    return host;
  } catch {
    return '';
  }
}

function getOperationProgressPercent(confirmations, requiredConfirmations, confirmed) {
  if (confirmed) {
    return 100;
  }

  if (!requiredConfirmations) {
    return 0;
  }

  return Math.min(100, (confirmations / requiredConfirmations) * 100);
}

export function normalizeDepositOperation(operation) {
  const currency = operation?.currency;
  const asset = currency?.code || '';
  const assetDecimals = getCurrencyDecimals(currency);
  const amountUsd = toDecimalString(getOperationUsdAmount(operation));
  const amountAsset = toDecimalString(getOperationAssetAmount(operation));
  const confirmations = Number.parseInt(operation?.confirmation ?? 0, 10) || 0;
  const requiredConfirmations = Number.parseInt(operation?.confirmation_required ?? 0, 10) || 0;
  const confirmed = operation?.confirmed === true
    || (requiredConfirmations > 0 && confirmations >= requiredConfirmations);
  const explorerUrl = getExplorerUrl(operation);
  const txHash = operation?.tx_hash || '';

  return {
    raw: operation,
    key: getDepositOperationKey(operation),
    txHash,
    txHashFull: txHash,
    address: operation?.address || '',
    amountUsd,
    amountUsdLabel: formatUsdAmount(amountUsd),
    amountAsset,
    amountAssetLabel: formatAssetAmount(amountAsset, asset, assetDecimals),
    asset,
    assetDecimals,
    currencyIconUrl: getCurrencyIconUrl(currency),
    networkRaw: operation?.network || '',
    network: formatNetworkName(operation?.network),
    confirmations,
    totalConfirmations: requiredConfirmations,
    confirmed,
    progressPercent: getOperationProgressPercent(confirmations, requiredConfirmations, confirmed),
    explorerUrl,
    explorerName: getExplorerName(explorerUrl),
  };
}

function normalizeDepositPayload(payload) {
  if (payload && typeof payload === 'object') {
    return payload;
  }

  return {
    cardId: payload,
  };
}

function getDepositFlowKey(payload) {
  return [
    payload.cardId || '',
    payload.addressId || '',
    payload.amount || '',
    payload.asset || '',
    payload.network || '',
    payload.address || '',
  ].join(':');
}

function saveSession(session) {
  sessionStorage.setItem(DEPOSIT_SESSION_KEY, JSON.stringify(session));
}

function emit(session) {
  saveSession(session);
  listeners.forEach((listener) => listener(session));
}

export function getDepositSession() {
  try {
    return JSON.parse(sessionStorage.getItem(DEPOSIT_SESSION_KEY) ?? 'null');
  } catch {
    return null;
  }
}

export function setDepositCard(cardId) {
  emit({
    ...(getDepositSession() ?? {}),
    cardId,
  });
}

export function setDepositReturnPath(returnPath) {
  emit({
    ...(getDepositSession() ?? {}),
    returnPath,
  });
}

export function clearDepositSession() {
  sessionStorage.removeItem(DEPOSIT_SESSION_KEY);
  listeners.forEach((listener) => listener(null));
}

export function subscribeDepositSession(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

export function cancelDepositFlow() {
  activeFlow?.cancel();
  activeFlow = null;
}

export function startDepositFlow(payloadInput = {}, options = {}) {
  const payload = normalizeDepositPayload(payloadInput);
  const flowKey = options.preserveSession && payload.flowKey
    ? payload.flowKey
    : getDepositFlowKey(payload);
  const current = getDepositSession();
  const shouldPreserveSession = options.preserveSession
    && current?.flowKey === flowKey
    && ['awaiting', 'confirming'].includes(current.status);

  if (
    activeFlow &&
    current?.flowKey === flowKey &&
    ['awaiting', 'confirming'].includes(current.status)
  ) {
    return activeFlow;
  }

  cancelDepositFlow();

  const operationKeys = shouldPreserveSession ? current.operationKeys || [] : [];
  const ignoredOperationKeys = shouldPreserveSession ? current.ignoredOperationKeys || [] : [];
  const operationBaselineCaptured = shouldPreserveSession
    ? current.operationBaselineCaptured === true
      || operationKeys.length > 0
      || ignoredOperationKeys.length > 0
    : false;
  const nextPayload = {
    ...payload,
    flowKey,
    operationKeys,
    ignoredOperationKeys,
    operationBaselineCaptured,
  };

  emit({
    ...(shouldPreserveSession ? current : current ?? {}),
    ...nextPayload,
    flowKey,
    status: shouldPreserveSession ? current.status : 'awaiting',
    receivedAmount: shouldPreserveSession ? current.receivedAmount ?? '0' : '0',
    receivedAssetAmount: shouldPreserveSession ? current.receivedAssetAmount ?? '0' : '0',
    operations: shouldPreserveSession ? current.operations || [] : [],
    operationKeys,
    ignoredOperationKeys,
    operationBaselineCaptured,
    shouldOpenStatusFlow: shouldPreserveSession ? current.shouldOpenStatusFlow : false,
  });

  activeFlow = pollDepositOperations({ payload: nextPayload });

  return activeFlow;
}

export function resumeDepositFlow(payloadInput = {}) {
  return startDepositFlow(payloadInput, { preserveSession: true });
}

function getResponseData(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function toDecimalString(value) {
  const decimal = parseDecimalInput(value);

  return decimal ? decimal.toString() : '0';
}

function summarizeOperations(operations) {
  return operations.reduce((summary, operation) => {
    const isConfirmed = operation.confirmed;
    const explorerUrl = summary.explorerUrl || operation.explorerUrl;
    const nextSummary = {
      receivedAmount: addDecimalStrings(summary.receivedAmount, operation.amountUsd),
      receivedAssetAmount: addDecimalStrings(summary.receivedAssetAmount, operation.amountAsset),
      confirmations: Math.max(summary.confirmations, operation.confirmations),
      totalConfirmations: Math.max(summary.totalConfirmations, operation.totalConfirmations),
      txHash: summary.txHash || operation.txHash,
      txHashFull: summary.txHashFull || operation.txHashFull,
      asset: summary.asset || operation.asset || '',
      assetDecimals: summary.assetDecimals ?? operation.assetDecimals,
      network: summary.network || operation.networkRaw || operation.network || '',
      explorerUrl,
      explorerName: summary.explorerName || operation.explorerName,
      hasPending: summary.hasPending || !isConfirmed,
      hasConfirmed: summary.hasConfirmed || isConfirmed,
    };

    if (isConfirmed) {
      nextSummary.confirmedAmount = addDecimalStrings(summary.confirmedAmount, operation.amountUsd);
      nextSummary.confirmedAssetAmount = addDecimalStrings(
        summary.confirmedAssetAmount,
        operation.amountAsset
      );
    } else {
      nextSummary.confirmedAmount = summary.confirmedAmount;
      nextSummary.confirmedAssetAmount = summary.confirmedAssetAmount;
    }

    return nextSummary;
  }, {
    receivedAmount: '0',
    confirmedAmount: '0',
    receivedAssetAmount: '0',
    confirmedAssetAmount: '0',
    confirmations: 0,
    totalConfirmations: 0,
    txHash: '',
    txHashFull: '',
    asset: '',
    assetDecimals: null,
    network: '',
    explorerUrl: '',
    explorerName: '',
    hasPending: false,
    hasConfirmed: false,
  });
}

function pollDepositOperations({ payload }) {
  let timer;
  let cancelled = false;
  let operationBaselineCaptured = payload.operationBaselineCaptured === true;
  const visibleOperationKeys = new Set(payload.operationKeys || []);
  const ignoredOperationKeys = new Set(payload.ignoredOperationKeys || []);
  const asset = payload.asset || 'USDT';
  const network = payload.network || DEPOSIT_NETWORK_SHORT;
  const requiredAmount = toDecimalString(
    payload.expectedAmountUsd
    || payload.balanceAmountUsd
    || payload.minimumAmount
    || payload.amount
  );
  const requiredAssetAmount = toDecimalString(payload.amount);
  const useAssetThreshold = decimalGte(requiredAssetAmount, '0')
    && !parseDecimalInput(requiredAssetAmount)?.eq(0);

  const cleanup = () => {
    window.clearTimeout(timer);
  };

  const poll = async () => {
    if (cancelled) return;

    if (!payload.cardId || !payload.addressId) {
      timer = window.setTimeout(poll, POLL_INTERVAL_MS);
      return;
    }

    try {
      const response = await cardsApi.getDepositOperations({
        id: payload.cardId,
        addressId: payload.addressId,
      });
      if (cancelled) return;

      const operations = getResponseData(response)
        .map(normalizeDepositOperation)
        .filter((operation) => operation.key);
      const trackedOperations = [];

      if (!operationBaselineCaptured) {
        operations.forEach((operation) => {
          ignoredOperationKeys.add(operation.key);
        });
        operationBaselineCaptured = true;

        emit({
          ...payload,
          status: 'awaiting',
          asset,
          network,
          receivedAmount: '0',
          receivedAssetAmount: '0',
          amountUsd: formatUsdAmount('0'),
          amountAsset: formatAssetAmount('0', asset),
          confirmations: 0,
          totalConfirmations: 0,
          operations: [],
          operationKeys: Array.from(visibleOperationKeys),
          ignoredOperationKeys: Array.from(ignoredOperationKeys),
          operationBaselineCaptured,
          shouldOpenStatusFlow: false,
        });
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }

      operations.forEach((operation) => {
        if (ignoredOperationKeys.has(operation.key)) {
          return;
        }

        if (visibleOperationKeys.has(operation.key)) {
          trackedOperations.push(operation);
          return;
        }

        visibleOperationKeys.add(operation.key);
        trackedOperations.push(operation);
      });

      if (!trackedOperations.length) {
        emit({
          ...payload,
          status: 'awaiting',
          asset,
          network,
          receivedAmount: '0',
          receivedAssetAmount: '0',
          amountUsd: formatUsdAmount('0'),
          amountAsset: formatAssetAmount('0', asset),
          confirmations: 0,
          totalConfirmations: 0,
          operations: [],
          operationKeys: Array.from(visibleOperationKeys),
          ignoredOperationKeys: Array.from(ignoredOperationKeys),
          operationBaselineCaptured,
          shouldOpenStatusFlow: false,
        });
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }

      const summary = summarizeOperations(trackedOperations);
      const thresholdAmount = useAssetThreshold ? requiredAssetAmount : requiredAmount;
      const receivedThresholdAmount = useAssetThreshold
        ? summary.receivedAssetAmount
        : summary.receivedAmount;
      const confirmedThresholdAmount = useAssetThreshold
        ? summary.confirmedAssetAmount
        : summary.confirmedAmount;
      const isEnoughReceived = decimalGte(receivedThresholdAmount, thresholdAmount);
      const isEnoughConfirmed = decimalGte(confirmedThresholdAmount, thresholdAmount);
      const nextStatus = isEnoughConfirmed ? 'success' : isEnoughReceived ? 'confirming' : 'awaiting';
      const receivedAmount = decimalGte(summary.confirmedAmount, summary.receivedAmount)
        ? summary.confirmedAmount
        : summary.receivedAmount;
      const receivedAssetAmount = decimalGte(
        summary.confirmedAssetAmount,
        summary.receivedAssetAmount
      )
        ? summary.confirmedAssetAmount
        : summary.receivedAssetAmount;
      const operationAsset = summary.asset || asset;
      const operationNetwork = formatNetworkName(summary.network || network);

      emit({
        ...payload,
        status: nextStatus,
        amountUsdt: String(receivedAssetAmount),
        amountAsset: formatAssetAmount(
          receivedAssetAmount,
          operationAsset,
          summary.assetDecimals
        ),
        asset: operationAsset,
        amountUsd: formatUsdAmount(receivedAmount),
        network: operationNetwork,
        operations: trackedOperations,
        operationKeys: Array.from(visibleOperationKeys),
        ignoredOperationKeys: Array.from(ignoredOperationKeys),
        operationBaselineCaptured,
        confirmations: summary.confirmations,
        totalConfirmations: summary.totalConfirmations,
        receivedAmount,
        receivedAssetAmount,
        txHash: summary.txHash,
        txHashFull: summary.txHashFull,
        explorerUrl: summary.explorerUrl,
        explorerName: summary.explorerName,
        date: formatDepositDate(),
        requiredAmount,
        requiredAssetAmount,
        shouldOpenStatusFlow: isEnoughReceived,
      });

      if (!isEnoughConfirmed) {
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
      }
    } catch (error) {
      if (cancelled) return;

      emit({
        ...payload,
        status: 'awaiting',
        asset,
        network,
        receivedAmount: '0',
        operationBaselineCaptured,
        error,
      });
      timer = window.setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  poll();

  return {
    cancel() {
      cancelled = true;
      cleanup();
    },
  };
}
